import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/react-hooks'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Search, Loader2 } from 'lucide-react'
import * as Query from '../../../../api/gql/query'

export interface RoleOption { id: string; key: string | null; name: string; isSystem: boolean }

interface Props {
  isOpen: boolean
  existingUserIds: Set<string>
  saving: boolean
  roles: RoleOption[]
  onClose: () => void
  onConfirm: (userId: string, roleId: string) => void
}

const defaultRoleId = (roles: RoleOption[]) =>
  roles.find((r) => r.key === 'SUBCONTRACTOR')?.id ?? roles[0]?.id ?? ''

const AddMemberDialog: React.FC<Props> = ({ isOpen, existingUserIds, saving, roles, onClose, onConfirm }) => {
  const { t } = useTranslation()
  // The org roster needs ORGANIZATION.VIEW, which an external project admin does
  // not hold — say so instead of rendering an empty candidate list.
  const { data, error } = useQuery(Query.USERS)
  const [query, setQuery] = useState('')
  const [picked, setPicked] = useState<string | null>(null)
  const [roleId, setRoleId] = useState<string>(() => defaultRoleId(roles))

  useEffect(() => {
    if (roleId || roles.length === 0) return
    setRoleId(defaultRoleId(roles))
  }, [roles, roleId])

  const q = query.toLowerCase()
  const candidates = (data?.users ?? []).filter((u: any) => {
    if (existingUserIds.has(u.id)) return false
    // The org roster is a management view and keeps deactivated accounts; adding
    // one to a project would only create work assigned to someone who cannot
    // sign in. Reactivate first, then add.
    if (u.deactivatedAt) return false
    const hay = `${u.name ?? ''} ${u.email ?? ''}`.toLowerCase()
    return hay.includes(q)
  })

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('Members_add_dialog_title')}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">{t('Members_add_dialog_pick_user')}</p>

        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder={t('Members_search_placeholder')} className="h-9 pl-8 text-sm" />
        </div>

        <div className="max-h-[260px] overflow-y-auto border rounded-md">
          {candidates.map((u: any) => (
            <button key={u.id} type="button" onClick={() => setPicked(u.id)}
              className={`w-full text-left px-3 py-2 border-b last:border-b-0 text-sm transition-colors ${
                picked === u.id ? 'bg-primary/10' : 'hover:bg-muted'}`}>
              <div className="font-medium">{u.name || u.email}</div>
              <div className="text-xs text-muted-foreground">{u.email}</div>
            </button>
          ))}
          {candidates.length === 0 && (
            <div className="px-3 py-4 text-sm text-muted-foreground text-center">
              {error ? t('Members_add_dialog_roster_denied') : '—'}
            </div>
          )}
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium">{t('Members_add_dialog_role')}</label>
          <Select value={roleId} onValueChange={setRoleId}>
            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              {roles.map((r) => (
                <SelectItem key={r.id} value={r.id}>{r.key ? t('role_' + r.key, r.name) : r.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t">
          <Button variant="outline" onClick={onClose} disabled={saving}>{t('cancel', 'Cancel')}</Button>
          <Button onClick={() => picked && onConfirm(picked, roleId)} disabled={!picked || !roleId || saving}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('Members_add_dialog_confirm')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default AddMemberDialog
